/**
 * CLASSIFICAR a solicitação — dizer de quem é o gasto.
 *
 * Fase 2 (decisão do Matheus, 15/09/2026 23h): a **Fila do Compras fundiu no Quadro** — *"'A
 * classificar' vira coluna do Quadro; a aba Fila some"*, porque *"duas pessoas vendo dois lugares
 * para o mesmo pedido é endereço duplicado"*. Este diálogo era da aba Fila; agora é o gesto da
 * coluna **"A classificar"**, e é ele que move o card dali (o servidor recalcula a coluna ao salvar).
 */
import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@/components/ui';
import { errMsg } from '@/lib/api-error';
import { useSolicitacaoActions } from '@/lib/solicitacoes-compra-store';

import { VinculoPicker } from './VinculoPicker';
import { buildVinculo, VINCULO_VAZIO, type VinculoState } from './vinculo-solicitacao';

export function ClassificarSolicitacaoDialog({
  solicitacaoId,
  vinculoInicial,
  onClose,
  onClassificada,
}: {
  solicitacaoId: string;
  /** Reclassificação parte do que já está lá; o card de "A classificar" começa vazio. */
  vinculoInicial?: VinculoState;
  onClose: () => void;
  /** Chamado depois de salvar — o Quadro reconsulta para o card mudar de coluna na hora. */
  onClassificada?: () => void;
}) {
  const toast = useToast();
  const { classificar } = useSolicitacaoActions();
  const [vinculo, setVinculo] = useState<VinculoState>(() => vinculoInicial ?? VINCULO_VAZIO);

  function salvar() {
    const v = buildVinculo(vinculo);
    if (!v) {
      toast.show('Escolha o vínculo (projeto, negócio, cliente ou uso interno).', { variant: 'error' });
      return;
    }
    classificar.mutate(
      { id: solicitacaoId, vinculo: v },
      {
        onSuccess: () => {
          toast.show('Solicitação classificada — o card saiu de "A classificar".', { variant: 'success' });
          onClassificada?.();
          onClose();
        },
        onError: (e) => toast.show(errMsg(e) ?? 'Não deu certo. Tente de novo.', { variant: 'error' }),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogHeader>
        <DialogTitle>Classificar solicitação</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <p className="text-sm text-ink-2 dark:text-ink-dark-2 mb-4">
          Diga de quem é o gasto. <strong>Projeto</strong> ou <strong>Negócio</strong> amarram o cliente e
          alimentam o realizado/indicadores; <strong>Cliente</strong> e <strong>Uso interno</strong> rastreiam o gasto.
        </p>
        <VinculoPicker value={vinculo} onChange={setVinculo} />
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={salvar} disabled={classificar.isPending}>
          {classificar.isPending ? 'Salvando…' : 'Classificar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
